
import Vue from 'vue';
import type { PropType } from 'vue';
import { mapState } from 'vuex';
import _pickBy from 'lodash/pickBy';
import _sortBy from 'lodash/sortBy';
import {
	RunnitNodeDef,
	RunnitNodeDefTag,
	RunnitNodeDefTagType,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_DEF_TOOL_TYPE,
} from '@run-diffusion/shared';

interface ToolGroup {
	key: string
	tagId: string | null
	label: string
	defs: RunnitNodeDef[]
}

export default Vue.extend({
	name: 'RunnitToolsListSelectByTag',
	props: {
		groupByTagType: {
			type: String as PropType<RunnitNodeDefTagType>,
			required: true,
		},
		selectedTagIds: {
			type: Object as PropType<Record<string, true>>,
			default: undefined,
		},
		selectedNodeDefIds: {
			type: Array as PropType<string[]>,
			default: undefined,
		},
		disabled: { type: Boolean, default: false },
		ungroupedLabel: { type: String, default: 'Uncategorized' },
	},
	data () {
		return {
			expandedPanelIndices: [],
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'publicRunnitNodeDefs',
			'companyRunnitNodeDefs',
			'loadingPublicRunnitNodeDefs',
			'loadingCompanyRunnitNodeDefs',
			'publicRunnitNodeDefTagsMap',
		]),
		loading (): boolean {
			return !!(this.loadingPublicRunnitNodeDefs || this.loadingCompanyRunnitNodeDefs);
		},
		tagsById (): Record<string, RunnitNodeDefTag> {
			return this.publicRunnitNodeDefTagsMap || {};
		},
		nodeDefs (): RunnitNodeDef[] {
			const companyId: string = this.team?.companyId || null;
			return [
				...(this.publicRunnitNodeDefs || []),
				...(this.companyRunnitNodeDefs || []),
			].filter((d: RunnitNodeDef) => {
				if (d.isDeleted || d.appType !== RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT) return false;
				if (!this.user?.isAdmin && !d.isPublished) return false;
				if (d.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY) {
					return !!(companyId && d.companyIds?.[companyId]);
				}
				return true;
			});
		},
		selectedTagTruthMap (): Record<string, true> {
			return _pickBy(this.selectedTagIds || {}, Boolean) as Record<string, true>;
		},
		selectedNodeIdSet (): Set<string> {
			return new Set(
				(this.selectedNodeDefIds || []).filter((id: string) => id)
			);
		},
		groups (): ToolGroup[] {
			const { tagsById, groupByTagType, ungroupedLabel } = this;

			const tagSortIteratees = [
				(id: string) => tagsById[id]?.sortOrder ?? 0,
				(id: string) => tagsById[id]?.label || '',
			];

			const primaryTagIdForDef = (def: RunnitNodeDef): string | null => {
				const candidates = Object.keys(def.tags || {}).filter(
					(id) => tagsById[id]?.type === groupByTagType,
				);
				return _sortBy(candidates, tagSortIteratees)[0] || null;
			};

			const byTag = new Map<string, RunnitNodeDef[]>();
			const uncategorized: RunnitNodeDef[] = [];

			for (const def of this.nodeDefs) {
				const tagId = primaryTagIdForDef(def);
				if (tagId) {
					if (!byTag.has(tagId)) byTag.set(tagId, []);
					byTag.get(tagId).push(def);
				} else {
					uncategorized.push(def);
				}
			}

			const tagGroups: ToolGroup[] = _sortBy(Array.from(byTag.keys()), tagSortIteratees)
				.map((tagId) => ({
					key: `tag-${tagId}`,
					tagId,
					label: tagsById[tagId]?.label || tagId,
					defs: _sortBy(byTag.get(tagId), 'title'),
				}));

			if (uncategorized.length) {
				tagGroups.push({
					key: 'uncategorized',
					tagId: null,
					label: ungroupedLabel,
					defs: _sortBy(uncategorized, 'title'),
				});
			}

			return tagGroups;
		},
		/** Stable fingerprint so we reset expansion state when group list/contents change, not on every re-render. */
		groupStructureSignature (): string {
			return this.groups.map((g: ToolGroup) => `${g.key}:${g.defs.length}`).join('|');
		},
	},
	watch: {
		groupStructureSignature: {
			immediate: true,
			handler () {
				this.expandedPanelIndices = [];
			},
		},
	},
	methods: {
		getGroupCheckedCount (group: ToolGroup): number {
			if (this.isTagSelected(group.tagId)) {
				return group.defs.length;
			}
			return group.defs.filter((def: RunnitNodeDef) => this.selectedNodeIdSet.has(def.id)).length;
		},
		/** Tag-level checkbox: partial blocklist (some defs, not all) without “block whole tag”. */
		isTagGroupCheckboxIndeterminate (group: ToolGroup): boolean {
			if (!group.tagId || this.isTagSelected(group.tagId)) return false;
			const checked: number = this.getGroupCheckedCount(group);
			const total: number = group.defs.length;
			return checked > 0 && checked < total;
		},
		groupCountLabel (group: ToolGroup): string {
			const total: number = group.defs.length;
			if (this.isTagSelected(group.tagId)) {
				return 'block all current & future';
			}
			const checked: number = this.getGroupCheckedCount(group);
			if (checked > 0) {
				return `block ${checked}/${total}`;
			}
			return `${total}`;
		},
		isTagSelected (tagId: string | null): boolean {
			return !!(tagId && this.selectedTagTruthMap[tagId]);
		},
		isDefSelected (def: RunnitNodeDef, group: ToolGroup): boolean {
			return this.isTagSelected(group.tagId) || this.selectedNodeIdSet.has(def.id);
		},
		onTagGroupCheckboxInput (group: ToolGroup, checked: boolean) {
			if (!group.tagId) return;
			const nextTags: Record<string, true> = { ...this.selectedTagTruthMap };
			const nextIdSet: Set<string> = new Set(this.selectedNodeIdSet);

			if (checked) {
				nextTags[group.tagId] = true;
				group.defs.forEach((def: RunnitNodeDef) => nextIdSet.delete(def.id));
			} else {
				delete nextTags[group.tagId];
			}
			this.emitUpdate(nextTags, nextIdSet);
		},
		onDefRowClick (def: RunnitNodeDef, group: ToolGroup) {
			if (this.disabled || this.isTagSelected(group.tagId)) return;
			this.onDefToggle(def, group, !this.isDefSelected(def, group));
		},
		onDefToggle (def: RunnitNodeDef, group: ToolGroup, checked: boolean) {
			if (this.isTagSelected(group.tagId)) return;
			const nextIdSet = new Set(this.selectedNodeIdSet);
			checked ? nextIdSet.add(def.id) : nextIdSet.delete(def.id);
			this.emitUpdate({ ...this.selectedTagTruthMap }, nextIdSet);
		},
		emitUpdate (tags: Record<string, true>, idSet: Set<string>) {
			this.$emit('selected-update', {
				selectedTagIds: tags,
				selectedNodeDefIds: Array.from(idSet).sort(),
			});
		},
	},
});
